import { useApp, useCollectionItem } from '@hb/react'
import {
  capitalizeFirstLetterOfEachWord,
  CollectionItem,
  DropdownField,
  DropdownOption,
  FieldTypes,
  insurersCollection,
  PracticeContractTemplate,
  Template,
  TemplateId,
  TemplateKey,
  templateTypeNames,
  WithId,
} from '@hb/shared'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelectedPractice } from '../../../hooks/backend/auth/useSelectedPractice'
import { StandaloneInput } from '../../forms/Input'
import { InputRef } from '../../forms/Input/types'
import { useTemplateData } from '../contexts/data'
import { useTemplates } from '../hooks/useTemplates'

type TemplateCollectionItems = Array<CollectionItem<Template>>
export const TemplateMenu: React.FC<{
  onChange: (updated: { template: WithId<Template>; practiceId: string | null }) => void
  templateType: TemplateKey
  insuranceProviderId: string | null
  templateId: TemplateId | null
  focusOnMount?: boolean
  large?: boolean
}> = ({ insuranceProviderId, onChange, templateType, templateId, focusOnMount }) => {
  const {
    shortcutArgs: { practice },
  } = useTemplateData()
  const inputRef = useRef<InputRef>(null)
  const { id: appPracticeId } = useSelectedPractice()
  const { appName } = useApp()
  const practiceId = useMemo(() => {
    if (appName === 'providers-app') return appPracticeId
    return practice?.id ?? null
  }, [appName, appPracticeId, practice])
  const {
    adminTemplates: { items: allAdminTemplateItems },
    practiceTemplates: { items: practiceTemplateItems },
  } = useTemplates(practiceId, templateType)
  // internal practice contract templates cannot be sent (the server rejects them) — hide them
  const adminTemplateItems = useMemo(() => {
    if (templateType !== 'practiceContract' || !allAdminTemplateItems) return allAdminTemplateItems
    return allAdminTemplateItems.filter(t => !(t as WithId<PracticeContractTemplate>).isInternal)
  }, [allAdminTemplateItems, templateType])
  const { item: insuranceProvider } = useCollectionItem(insurersCollection, insuranceProviderId)
  const { insuranceProviderTemplates, otherTemplates, practiceTemplates } = useMemo(() => {
    if (!adminTemplateItems && !practiceTemplateItems) {
      return {
        otherTemplates: [] as TemplateCollectionItems,
        insuranceProviderTemplates: [] as TemplateCollectionItems,
        practiceTemplates: [] as TemplateCollectionItems,
      }
    }
    if (!insuranceProvider) {
      return {
        otherTemplates: adminTemplateItems,
        insuranceProviderTemplates: [] as TemplateCollectionItems,
        practiceTemplates: practiceTemplateItems,
      }
    }
    const adminTemplates = adminTemplateItems.reduce(
      (acc, curr) => {
        switch (curr.id) {
          case insuranceProvider.defaultTemplateId:
          case insuranceProvider.authTemplateId:
          case insuranceProvider.claimTemplateId:
          case insuranceProvider.fullyInsuredTemplateId:
          case insuranceProvider.selfFundedTemplateId:
            acc.insuranceProviderTemplates.push(curr)
            break
          default:
            acc.otherTemplates.push(curr)
        }
        return acc
      },
      {
        insuranceProviderTemplates: [] as CollectionItem<Template>[],
        otherTemplates: [] as CollectionItem<Template>[],
      },
    )
    return {
      ...adminTemplates,
      practiceTemplates: practiceTemplateItems,
    }
  }, [adminTemplateItems, practiceTemplateItems, insuranceProvider])

  const templateOptions = useMemo(() => {
    const options: DropdownOption[] = []
    if (insuranceProviderTemplates.length) {
      options.push({
        title: `${insuranceProvider?.name} Templates`,
        options: insuranceProviderTemplates.map(t => ({
          id: t.id,
          text: t.name,
        })),
      })
    }

    if (otherTemplates.length) {
      if (insuranceProviderTemplates.length) {
        options.push({
          title: 'Additional Templates',
          options: otherTemplates.map(t => ({
            id: t.id,
            text: t.name,
          })),
        })
      } else {
        options.push(
          ...otherTemplates.map(t => ({
            id: t.id,
            text: t.name,
          })),
        )
      }
    }

    if (practiceId && practiceTemplates.length) {
      const practiceOptions = practiceTemplates.map(t => ({
        id: `${practiceId}-${t.id}`,
        text: t.name,
      }))
      if (appName === 'app') {
        options.push({
          title: 'Practice Templates',
          options: practiceOptions,
        })
      } else {
        options.push(...practiceOptions)
      }
    }
    return options
  }, [
    insuranceProvider,
    insuranceProviderTemplates,
    otherTemplates,
    practiceTemplates,
    practiceId,
    appName,
  ])

  const field = useMemo<DropdownField>(
    () => ({
      type: FieldTypes.DROPDOWN,
      placeholder: `${capitalizeFirstLetterOfEachWord(templateTypeNames[templateType])} Template`,
      searchable: true,
      options: templateOptions,
      // optional: true,
    }),
    [templateOptions, templateType],
  )

  const onSelect = useCallback(
    (selected: { template: WithId<Template>; practiceId: string | null }) => {
      if (selected.template.templateText?.length || selected.template.isExternalPdf) {
        onChange(selected)
      }
    },
    [onChange],
  )

  const value = useMemo(() => {
    if (templateId) {
      return templateId.practiceId
        ? `${templateId.practiceId}-${templateId.templateId}`
        : templateId.templateId
    }
    return null
  }, [templateId])

  useEffect(() => {
    if (focusOnMount) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    }
  }, [focusOnMount])

  return (
    <StandaloneInput
      style={{
        padding: '0.3rem 0.5rem',
        // marginLeft: '0.2rem',
      }}
      theme="detailed"
      field={field}
      value={value}
      ref={inputRef}
      onChange={async v => {
        const idParts = v.split('-')
        const templatePracticeId = idParts.length === 2 ? idParts[0] : null
        const templateId = idParts.length === 2 ? idParts[1] : idParts[0]

        const updated =
          idParts.length === 2
            ? practiceTemplates?.find(o => o.id === templateId)
            : (insuranceProviderTemplates?.find(o => o.id === templateId) ??
              otherTemplates?.find(o => o.id === templateId))
        if (updated) {
          onSelect({ template: updated, practiceId: templatePracticeId })
          return { success: 'success' }
        }
        return { error: `Couldn't find template with id: ${v}` }
      }}
    />
  )
}
